/**
 * LocationMapPicker — web implementation with interactive Mapbox GL JS.
 *
 * Mirrors the native UX: user drags the map; the centered pin overlay
 * represents the selected location. The centre is reported only when the host
 * actually moved it — see `handleMoveEnd`.
 *
 * If the tiles cannot be loaded it degrades to a state that says so, points
 * the host back at the address field that still drives the location, and
 * offers a retry — never to a coordinate readout.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import Map, { type MapRef, type ViewStateChangeEvent } from 'react-map-gl/mapbox'
import { MapPin } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { COLORS, FONTS } from '../../lib/brand'
import { RADIUS, SPACING, INTERACTION } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors } from '../../lib/theme/tokens'
import { WEB_MAP_CANVAS_STYLE, WEB_MAP_SCROLL_ZOOM } from '../../lib/maps/web-map-gestures'
import { useLockPageScrollOverMap } from '../../lib/maps/use-web-map-wheel-lock'
import {
  applyMapboxWebConfig,
  ensureMapboxWeb,
  hasMapboxWebToken,
  MAPBOX_WEB_NETWORK_ERROR,
  MAPBOX_WEB_STYLE_URL,
  MAPBOX_WEB_TOKEN,
} from '../../lib/maps/mapbox-web'

ensureMapboxWeb()

const MAP_NETWORK_ERROR = MAPBOX_WEB_NETWORK_ERROR

/**
 * The map fills whatever vertical space the parent hands it — a picker is the
 * one element on the location step that should eat every available pixel —
 * but never collapses below this on short screens.
 */
const MIN_MAP_HEIGHT = 240

interface LocationMapPickerProps {
  center: [number, number]
  /** The host PICKED this point. Never the map reporting where it happens to be. */
  onLocationPicked: (lat: number, lng: number) => void
}

export function LocationMapPicker({ center, onLocationPicked }: LocationMapPickerProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const mapRef = useRef<MapRef>(null)
  const hostRef = useRef<View>(null)
  useLockPageScrollOverMap(hostRef)
  const [mapFailed, setMapFailed] = useState(false)

  const [lng, lat] = center

  useEffect(() => {
    setMapFailed(false)
  }, [isDark])

  /**
   * What the host sees when the tiles do not arrive.
   *
   * It used to print `13.6929, -89.2182`. A decimal pair is a developer
   * readout: it does not tell a host where the pin is, it does not tell them
   * the map broke rather than their address, and there is nothing to do with
   * it. What is actually true in this state is that the address field above
   * still drives the location — `handleNext` forward-geocodes it and writes
   * the resulting coordinates — so the step is not blocked. That is what the
   * box says now, and when the tiles are merely unreachable it also offers
   * the one action that can change the situation.
   *
   * `onRetry` is omitted when there is no Mapbox key at all: retrying a
   * request that cannot be built is a button that lies.
   */
  const renderFallback = (onRetry?: () => void) => (
    <View
      style={{
        flex: 1,
        minHeight: MIN_MAP_HEIGHT,
        borderRadius: RADIUS.lg,
        backgroundColor: colors.surfaceSubtle,
        borderWidth: 1,
        borderColor: colors.border,
        alignItems: 'center',
        justifyContent: 'center',
        gap: SPACING.sm,
        padding: SPACING.lg,
      }}
      testID="location.map-unavailable"
    >
      <MapPin size={28} color={colors.textMuted} strokeWidth={1.5} />
      <Text
        style={{
          fontSize: 15,
          fontFamily: FONTS.body,
          color: colors.textPrimary,
          textAlign: 'center',
        }}
      >
        {t('listing.map_unavailable_title')}
      </Text>
      <Text
        style={{
          fontSize: 13,
          lineHeight: 18,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          textAlign: 'center',
          maxWidth: 320,
        }}
      >
        {t('listing.map_unavailable_body')}
      </Text>
      {onRetry ? (
        <Pressable
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel={t('common.retry')}
          testID="location.map-retry"
          style={({ pressed }) => ({
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: INTERACTION.minTouchTarget,
            paddingHorizontal: SPACING.lg,
            borderRadius: RADIUS.full,
            borderWidth: 1,
            borderColor: colors.border,
            backgroundColor: colors.surface,
            opacity: pressed ? 0.72 : 1,
            // The container's gap binds the three lines of copy; the action is
            // a separate object and needs to read as one.
            marginTop: SPACING.xs,
          })}
        >
          <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textAccent }}>
            {t('common.retry')}
          </Text>
        </Pressable>
      ) : null}
    </View>
  )

  const handleMapError = useCallback((event: { error?: unknown }) => {
    const message = mapErrorMessage(event.error ?? event)
    if (MAP_NETWORK_ERROR.test(message)) {
      setMapFailed(true)
      return
    }
    setMapFailed(true)
  }, [])

  // La MISMA configuracion Standard que el nativo; el picker la monta sin
  // extrusion 3D porque es una tarea de precision.
  const handleLoad = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (map) applyMapboxWebConfig(map, 'picker', isDark)
  }, [isDark])

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (map) applyMapboxWebConfig(map, 'picker', isDark)
  }, [isDark])

  // No key: nothing to retry. Tiles failed: a fresh <Map> is a fresh request,
  // so clearing the flag remounts the provider rather than reloading the app.
  if (!hasMapboxWebToken()) return renderFallback()
  if (mapFailed) return renderFallback(() => setMapFailed(false))

  /**
   * A programmatic recentre fires `moveend` too — the `center` prop changing, the style
   * reloading on a theme flip — and reporting those as the host's choice would let the map
   * claim a pin nobody dropped. Mapbox attaches `originalEvent` only when the move came from
   * a real pointer, wheel or touch, which is exactly the line we need.
   */
  const handleMoveEnd = (event: { originalEvent?: unknown } | ViewStateChangeEvent) => {
    const originalEvent = 'originalEvent' in event ? event.originalEvent : undefined
    if (!originalEvent) return
    const c = mapRef.current?.getCenter()
    if (c) onLocationPicked(c.lat, c.lng)
  }

  return (
    <View
      ref={hostRef}
      style={{
        flex: 1,
        minHeight: MIN_MAP_HEIGHT,
        borderRadius: RADIUS.lg,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: colors.border,
        position: 'relative',
      }}
    >
      <Map
        ref={mapRef}
        initialViewState={{ longitude: lng, latitude: lat, zoom: 14 }}
        style={WEB_MAP_CANVAS_STYLE}
        mapboxAccessToken={MAPBOX_WEB_TOKEN}
        mapStyle={MAPBOX_WEB_STYLE_URL}
        attributionControl={false}
        onLoad={handleLoad}
        pitchWithRotate={false}
        dragRotate={false}
        dragPan
        touchZoomRotate
        scrollZoom={WEB_MAP_SCROLL_ZOOM}
        maxZoom={18}
        minZoom={8}
        onMoveEnd={handleMoveEnd}
        onError={handleMapError}
      />

      {/* Centered pin overlay (non-interactive, mirrors native) */}
      <View
        style={{
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MapPin
          size={32}
          color={COLORS.coral.DEFAULT}
          fill={COLORS.coral.DEFAULT}
          strokeWidth={2}
          style={{ marginBottom: 32 }}
        />
      </View>

      {/* No coordinate readout. A pill printing "13.6929, -89.2182" over the
          map is a developer readout, not an address: nothing else in the host
          wizard shows raw decimals, and the native picker
          (LocationMapPicker.tsx) never had one — the pin already says where the
          point is. Web now matches native. */}
    </View>
  )
}

function mapErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>
    const nested = record.error ?? record.message ?? record.statusText
    if (typeof nested === 'string') return nested
    if (nested instanceof Error) return nested.message
  }
  return String(error)
}
